// Unit conversion utilities for height and weight

/**
 * Convert height for display - assumes database stores metric format (cm)
 * @param height Height string from database in format "188 cm"
 * @param toMetric Boolean to indicate display format preference
 * @returns Converted height string for display
 */
export function convertHeight(height: string | undefined, toMetric: boolean): string {
  if (!height) return 'N/A';
  
  // Database stores metric format, so height should be like "188 cm"
  const cm = parseInt(height.replace(/[^\d]/g, ''));
  if (isNaN(cm) || cm < 120 || cm > 250) {
    return 'N/A';
  }
  
  if (toMetric) {
    // User wants metric display - return as is or ensure cm unit
    return height.includes('cm') ? height : `${cm} cm`;
  } else {
    // User wants imperial display - convert from cm to feet/inches
    const totalInches = Math.round(cm / 2.54);
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;
    return `${feet}'${inches}"`;
  }
}

/**
 * Convert weight for display - assumes database stores metric format (kg)
 * @param weight Weight string from database in format "75 kg"
 * @param toMetric Boolean to indicate display format preference
 * @returns Converted weight string for display
 */
export function convertWeight(weight: string | undefined, toMetric: boolean): string {
  if (!weight) return 'N/A';
  
  // Database stores metric format, so weight should be like "75 kg"
  const kg = parseInt(weight.replace(/[^\d]/g, ''));
  if (isNaN(kg) || kg < 30 || kg > 200) {
    return 'N/A';
  }
  
  if (toMetric) {
    // User wants metric display - return as is or ensure kg unit
    return weight.includes('kg') ? weight : `${kg} kg`;
  } else {
    // User wants imperial display - convert from kg to lbs
    const lbs = Math.round(kg / 0.453592);
    return `${lbs} lbs`;
  }
}